'use client';

import { useEffect, useMemo } from 'react';

import { useIsDesktop, useIsLandscape } from '@/hooks/useViewport';

import { useAppState } from '@/state/hooks/app.state.hook';

const applyBackgroundImage = (el: HTMLElement, imageUrl: string) => {
  el.style.backgroundImage = `url(${imageUrl})`;
  el.style.backgroundSize = 'cover';
  el.style.backgroundPosition = 'center';
  el.style.backgroundRepeat = 'no-repeat';
  el.style.backgroundAttachment = 'fixed';
};

const clearBackgroundImage = (el: HTMLElement) => {
  el.style.backgroundImage = '';
};

export const usePortalBackground = () => {
  const { initialBoot } = useAppState();
  const isDesktop = useIsDesktop();
  const isLandscape = useIsLandscape();

  const backgroundImage = useMemo(() => {
    const general = initialBoot?.theme?.general;
    if (!general) return null;

    return isDesktop || isLandscape
      ? general.backgroundImageDesktop
      : general.backgroundImageMobile;
  }, [
    initialBoot?.theme?.general?.backgroundImageDesktop,
    initialBoot?.theme?.general?.backgroundImageMobile,
    isDesktop,
    isLandscape,
  ]);

  useEffect(() => {
    // Paint both <html> and <body> so the overscroll/rubber-band area shows
    // the image (not the General Background Color). Overscroll behavior is set
    // globally to `contain` in globals.css.
    const elements = [document.documentElement, document.body];

    elements.forEach((el) =>
      backgroundImage ? applyBackgroundImage(el, backgroundImage) : clearBackgroundImage(el)
    );

    return () => elements.forEach(clearBackgroundImage);
  }, [backgroundImage]);

  return { backgroundImage };
};
